/**
 * Repository - HttpAccountRepository
 *
 * Récupère les informations d'un utilisateur (agent/caissier) via l'API CardLife.
 *
 * Endpoint attendu:
 *   GET /api/v1/cbs-integration/users/infos/{codeUser}
 *
 * NOTE: Pour la recherche de compte CLIENT, utiliser plutôt
 * CardLifeCbsAccountLookup (POST /clients/balance).
 */

import { IAccountRepository } from '../../application/ports/output/IAccountRepository';
import { Account, AccountStatus, AccountType } from '../../domain/entities/Account';
import { AccountNumber } from '../../domain/value-objects/AccountNumber';
import { buildBearerAuthHeader } from '../http/auth';

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const CARDLIFE_BASE_URL = (ENV.VITE_CARDLIFE_BASE_URL ?? '').replace(/\/+$/g, '');
const ACCOUNT_LOOKUP_PATH =
  ENV.VITE_CARDLIFE_ACCOUNT_LOOKUP_PATH ?? '/api/v1/cbs-integration/users/infos';

function normalizeStatus(value: unknown): AccountStatus {
  const raw = typeof value === 'string' ? value.toUpperCase() : '';
  if (raw.includes('BLOCK')) return AccountStatus.BLOCKED;
  if (raw.includes('CLOS')) return AccountStatus.CLOSED;
  return AccountStatus.ACTIVE;
}

function normalizeType(value: unknown): AccountType {
  const raw = typeof value === 'string' ? value.toUpperCase() : '';
  if (raw.includes('SAV') || raw.includes('EPARG')) return AccountType.SAVINGS;
  if (raw.includes('JOIN')) return AccountType.JOINT;
  return AccountType.CURRENT;
}

function readString(obj: Record<string, any>, keys: string[]): string | undefined {
  for (const key of keys) {
    const val = obj?.[key];
    if (typeof val === 'string' && val.trim()) return val;
  }
  return undefined;
}

export class HttpAccountRepository implements IAccountRepository {
  async findByNumber(accountNumber: AccountNumber): Promise<Account | null> {
    return this.findByNumberString(accountNumber.value);
  }

  async findByNumberString(accountNumber: string): Promise<Account | null> {
    let accNum: AccountNumber;
    try {
      accNum = AccountNumber.create(accountNumber);
    } catch {
      return null;
    }

    const url = `${CARDLIFE_BASE_URL}${ACCOUNT_LOOKUP_PATH}/${encodeURIComponent(accNum.value)}`;
    const authHeader = buildBearerAuthHeader();

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
    });

    if (!response.ok) return null;

    let data: any;
    try {
      data = await response.json();
    } catch {
      return null;
    }

    // Mapping tolérant (adapter selon le JSON réel)
    const status = normalizeStatus(
      data?.status ?? data?.statut ?? data?.etat ?? data?.accountStatus
    );
    const type = normalizeType(
      data?.type ?? data?.accountType ?? data?.typeCompte ?? data?.type_compte
    );

    const primaryId =
      readString(data, ['clientId', 'idClient', 'id_client', 'titulaireId']) ?? accNum.value;
    const primaryName =
      readString(data, ['clientName', 'nomClient', 'nom_client', 'titulaireNom', 'titulaire']) ??
      'Titulaire';

    const holdersRaw =
      (Array.isArray(data?.holders) && data.holders) ||
      (Array.isArray(data?.titulaires) && data.titulaires) ||
      [];

    const holders =
      holdersRaw.length > 0
        ? holdersRaw
            .map((h: any, idx: number) => ({
              id: readString(h ?? {}, ['id', 'clientId', 'idClient', 'id_client']) ?? `${primaryId}-${idx}`,
              name: readString(h ?? {}, ['name', 'nom', 'clientName', 'nomClient']) ?? `Titulaire ${idx + 1}`,
              status: (typeof h?.status === 'string' && h.status.toLowerCase() === 'inactive') ? 'inactive' : 'active',
              isPrimary: idx === 0 || h?.isPrimary === true || h?.principal === true,
            }))
            // Forcer exactement 1 titulaire principal
            .map((h: any, idx: number) => ({ ...h, isPrimary: idx === 0 }))
        : [
            {
              id: primaryId,
              name: primaryName,
              status: 'active' as const,
              isPrimary: true,
            },
          ];

    return Account.create({
      accountNumber: accNum,
      type,
      status,
      holders,
      createdAt: new Date(),
      currency: 'XOF',
    });
  }

  async findByClientId(_clientId: string): Promise<Account[]> {
    return [];
  }

  async findAll(): Promise<Account[]> {
    return [];
  }

  async exists(accountNumber: AccountNumber): Promise<boolean> {
    const account = await this.findByNumber(accountNumber);
    return account !== null;
  }
}
